import { getBaseFilename, sanitizeUrl } from '@components/@general/preview-doc';
import useAuthStore from '@store/authstore';
import { useState, useEffect, useMemo } from 'react';
import { encryptUrl } from 'utils/koceng';

interface UseFetchFileUrlParams {
  filename: string;
  url: string;
}

interface UseFetchFileUrlReturn {
  fetchUrl: string | null;
  extFile: string;
  isLoading: boolean;
  error: string | null;
}

export function useFetchFileUrl({ filename, url }: UseFetchFileUrlParams): UseFetchFileUrlReturn {
  const token = useAuthStore((state) => state.token) || '';
  const user = useAuthStore((state) => state.user);
  const fingerprint = useAuthStore((state) => state.fingerprint) || '';

  const [extFile, setExtFile] = useState<string>('');
  const [encryptedToken, setEncryptedToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cleanName = useMemo(() => getBaseFilename(filename), [filename]);
  const cleanUrl = useMemo(() => sanitizeUrl(url), [url]);

  // 1. Enkripsi User ID / Token
  useEffect(() => {
    let isMounted = true;

    if (user && typeof user === 'object' && 'id' in user && user.id) {
      encryptUrl(String(user.id))
        .then((data) => {
          if (isMounted) setEncryptedToken(data);
        })
        .catch((err) => {
          if (isMounted) setError(err.message || 'Gagal mengenkripsi token');
        });
    }

    return () => {
      isMounted = false;
    };
  }, [user]);

  // 2. Dapatkan Ekstensi File (dari filename atau proxy metadata)
  useEffect(() => {
    let isMounted = true;

    const getExtension = async () => {
      if (!cleanName) {
        if (isMounted) setExtFile('');
        return;
      }

      if (!cleanName.includes('.')) {
        try {
          const proxyUrl = `/api/get-meta-data-file?url=${encodeURIComponent(cleanUrl)}&token=${encodeURIComponent(token)}&fingerprint=${encodeURIComponent(fingerprint)}`;
          const response = await fetch(proxyUrl);
          
          if (!response.ok) {
            if (isMounted) setExtFile('');
            return;
          }

          const data = await response.json();
          const metaFilename = data?.filename || '';

          if (metaFilename) {
            const cleanMetaName = getBaseFilename(metaFilename);
            if (isMounted) setExtFile(cleanMetaName.split('.').pop()?.toLowerCase() || '');
            return;
          }

          if (isMounted) setExtFile('');
        } catch (e) {
          console.error('Error fetching metadata:', e);
          if (isMounted) setExtFile('');
        }
      } else {
        const ext = cleanName.split('.').pop()?.toLowerCase() || '';
        if (isMounted) setExtFile(ext);
      }
    };

    getExtension();

    return () => {
      isMounted = false;
    };
  }, [cleanName, cleanUrl, token, fingerprint]);

  // 3. Generate Fetch URL
  const fetchUrl = useMemo(() => {
    if (!encryptedToken || !url) return null;

    // Ambil domain saat ini (Client-side)
    const currentDomain = typeof window !== 'undefined' 
      ? window.location.origin 
      : process.env.NEXT_PUBLIC_APP_URL || '';

    const params = new URLSearchParams({
      ext: extFile,
      url: url,
      filename: cleanName,
      token: encryptedToken,
      fingerprint: fingerprint,
    });

    return `${currentDomain}/api/fetching-file?${params.toString()}`;
  }, [extFile, url, cleanName, encryptedToken, fingerprint]);

  return {
    fetchUrl,
    extFile,
    isLoading: !fetchUrl && !error,
    error,
  };
}